import {
  IconButton,
  Paper,
  Tooltip,
  Button as MaterialButton,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { makeStyles } from '@material-ui/styles';
import { AxiosError } from 'axios';
import { getQuarter, subQuarters } from 'date-fns';
import { useSnackbar } from 'notistack';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Link,
  Redirect,
  Switch,
  useLocation,
  useParams,
} from 'react-router-dom';
import { Role } from '../../api/user/Role';
import venturesAPI from '../../api/ventures';
import { Status, Venture } from '../../api/ventures/types/Venture';
import {
  Button,
  ConfirmButton,
  PageLoader,
  RouteACL,
  SnackMessage,
} from '../../components/common';
import { CreateSchedulingWizardButton } from '../../components/common/create-scheduling-wizard-button';
import DeleteButton from '../../components/common/delete-modal';
import EmptyDetails from '../../components/common/empty-details';
import EnrichProfileModal from '../../components/common/enrich-profile-modal';
import BaseLayout from '../../components/layout/base-layout';
import { Header } from '../../components/layout/main-layout';
import PageTabs from '../../components/ventures/page-tabs';
import { UserContext } from '../../contexts/user-context';
import {
  VentureContext,
  VentureProvider,
} from '../../contexts/venture-context';
import { getRoutePath, Pages } from '../../router/constants';
import { ProtectedRouteProps } from '../../router/type';
import { checkDynamicPath, encodeQuery } from '../../utils/functions';
import VentureAdditionalInfoPage from './venture-additional-info-page';
import VentureAssignmentsPage from './venture-assignments-page';
import VentureDetailsPage from './venture-details-page';
import VentureFoundersPage from './venture-founders-page';
import VentureGoalsPage from './venture-goals-page';
import VentureLogPage from './venture-log-page';
import VentureMentorHistoryPage from './venture-mentor-history-page';
import VentureNotesPage from './venture-notes-page';
import VentureQuarterlyReportFormPage from './venture-quarterly-report-form-page';
import VentureQuarterlyReportPage from './venture-quarterly-report-page';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: 0,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    padding: 40,
    flexGrow: 1,
  },
  requestLink: {
    minWidth: 187,
    whiteSpace: 'nowrap',
  },
  newSessionButton: {
    whiteSpace: 'nowrap',
    minWidth: 108,
  },
  linkButton: {
    whiteSpace: 'nowrap',
  },
});

function VenturePage({ user }: ProtectedRouteProps) {
  const classes = useStyles();
  const { ventureId } = useParams<{ ventureId: Venture['id'] }>();
  const location = useLocation<{
    prevPath?: string;
    openEnrichModal?: boolean;
  }>();
  const [prevPath] = useState(location.state?.prevPath);
  const { tokenData, hasAccessToAction, hasRole } = useContext(UserContext);
  const {
    venture,
    assignedMentors,
    ventureTags,
    loadVentureTags,
    isVentureLoading,
    ventureSpecializations,
    loadVenture,
    loadVentureSpecializations,
    updateVenture,
    isAccessDenied,
  } = useContext(VentureContext);
  const { enqueueSnackbar } = useSnackbar();
  const [sending, setSending] = useState(false);
  const [isEnrichModalOpen, setIsEnrichModalOpen] = useState(false);

  useEffect(() => {
    if (location.state?.openEnrichModal) {
      setIsEnrichModalOpen(true);
    }
  }, [location.state?.openEnrichModal]);

  const handleCloseEnrichModal = useCallback(() => {
    setIsEnrichModalOpen(false);
  }, []);

  const handleEnrichSuccess = useCallback(
    (updatedVenture: Venture) => {
      updateVenture(updatedVenture);
      loadVentureSpecializations(ventureId);
    },
    [updateVenture, loadVentureSpecializations, ventureId],
  );

  const currentPath = useMemo(
    () => `${location.pathname}${location.search}`,
    [location],
  );

  const linkActions = useMemo(() => {
    const lastQuarter = getQuarter(subQuarters(new Date(), 1));
    return [
      {
        id: 'quarter-report',
        label: `Q${lastQuarter} Report`,
        href: Pages.VENTURE_QUARTERLY_REPORT_FORM,
        access: 'venture.details.quarterReport',
        isContained: true,
        startIcon: <AddIcon />,
      },
    ];
  }, []);

  const { title, isReportPage } = useMemo(() => {
    const reportPages = [
      {
        title: 'Venture Quarterly Report',
        path: Pages.VENTURE_QUARTERLY_REPORT_FORM,
      },
    ];

    const targetReportPage = reportPages.find((page) =>
      checkDynamicPath(location.pathname, page.path),
    );

    return targetReportPage
      ? {
          title: targetReportPage.title,
          isReportPage: true,
        }
      : {
          title: venture?.ventureName || '',
          isReportPage: false,
        };
  }, [location.pathname, venture?.ventureName]);

  const isMentorNotParticipant = useMemo(() => {
    const isMentor = hasRole(Role.Mentor);

    if (!isMentor) {
      return false;
    }

    const identityId = tokenData?.identityid;
    const loadedAssignedMentors = assignedMentors.type === 'Success';

    if (!identityId || !loadedAssignedMentors) {
      return true;
    }

    return assignedMentors.payload.data.every(
      (m) => m !== tokenData.identityid,
    );
  }, [assignedMentors, hasRole, tokenData?.identityid]);

  const handleStatusChange = useCallback(
    async (status: Status) => {
      if (venture) {
        const prevStatus = venture.status;
        try {
          updateVenture({ ...venture, status });
          await venturesAPI.update(venture.id, { ...venture, status });
          enqueueSnackbar(
            `The venture status was successfully changed to '${status}'`,
            {
              variant: 'success',
            },
          );
        } catch (e: any) {
          const messageError = e.response?.data?.message;

          updateVenture({ ...venture, status: prevStatus });
          enqueueSnackbar(
            "An error occurred while changing the venture's status. Please, try again.",
            {
              content: (key, message) =>
                SnackMessage({
                  key,
                  message,
                  variant: 'error',
                  additionalMessage: messageError,
                }),
              variant: 'error',
            },
          );
          console.log('error status change', e);
        }
      }
    },
    [venture, updateVenture, enqueueSnackbar],
  );

  const handleSendVentureUpdate = async (onClose: () => any) => {
    try {
      setSending(true);
      await venturesAPI.sendVentureUpdateInvites([ventureId]);
      enqueueSnackbar('The profile update request was successfully sent', {
        variant: 'success',
      });
      setSending(false);
      onClose();
    } catch (e) {
      const messageError = (e as AxiosError).response?.data?.message;

      setSending(false);
      enqueueSnackbar(
        'An error occurred while sending profile update request. Please, try again.',
        {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: messageError,
            }),
          variant: 'error',
        },
      );
      throw e;
    }
  };

  // load venture's info
  useEffect(() => {
    if (!venture) {
      loadVenture(ventureId);
    }
  }, [ventureId, venture, loadVenture]);

  // load venture's specializations
  useEffect(() => {
    if (!ventureSpecializations) {
      loadVentureSpecializations(ventureId);
    }
  }, [ventureSpecializations, ventureId, loadVentureSpecializations]);

  // load venture's tags
  useEffect(() => {
    if (!ventureTags) {
      loadVentureTags(ventureId);
    }
  }, [ventureTags, ventureId, loadVentureTags]);

  const backLink = useMemo(() => {
    return (
      <Tooltip
        title={isReportPage ? 'Back' : 'Back to Ventures'}
        data-testid='button-back-ventures'
      >
        <IconButton
          component={Link}
          to={
            isReportPage
              ? getRoutePath(Pages.VENTURE_DETAILS, {
                  ventureId,
                })
              : prevPath || Pages.ACTIVE_VENTURES
          }
        >
          <ArrowBackIcon />
        </IconButton>
      </Tooltip>
    );
  }, [isReportPage, ventureId, prevPath]);

  return (
    <BaseLayout user={user} fullHeight>
      {isVentureLoading ? (
        <PageLoader />
      ) : isAccessDenied ? (
        <EmptyDetails
          backLink={backLink}
          title='Access Denied'
          content='Unfortunately you do not have access to this page. Please contact program admin if you believe this is a mistake'
        />
      ) : !venture ? (
        <EmptyDetails backLink={backLink} />
      ) : (
        <>
          <Header
            title={title}
            onStatusChange={handleStatusChange}
            backLink={backLink}
            isMergedActions
            actions={[
              {
                key: 'new-session',
                label: 'New Session',
                hidden:
                  !hasAccessToAction('session.create') ||
                  isMentorNotParticipant,
                component: (
                  <MaterialButton
                    component={Link}
                    to={{
                      pathname: getRoutePath(Pages.NEW_SESSIONS),
                      state: {
                        ventureId: ventureId,
                        prevPath: currentPath,
                      },
                    }}
                    startIcon={<AddIcon />}
                    variant='contained'
                    color='primary'
                    className={classes.newSessionButton}
                    data-testid='button-create-session'
                  >
                    Session
                  </MaterialButton>
                ),
              },
              {
                key: 'new-goal',
                label: 'New Goal',
                hidden:
                  !hasAccessToAction('goal.create') || isMentorNotParticipant,
                component: (
                  <MaterialButton
                    component={Link}
                    to={{
                      pathname: Pages.NEW_GOAL,
                      state: {
                        prevPath: currentPath,
                      },
                      search:
                        venture && venture.id && venture.ventureName
                          ? `?venture=${encodeQuery({
                              id: venture.id,
                              ventureName: venture.ventureName,
                            })}`
                          : '',
                    }}
                    startIcon={<AddIcon />}
                    variant='contained'
                    color='primary'
                    data-testid='button-create-goal'
                  >
                    Goal
                  </MaterialButton>
                ),
              },
              {
                key: 'scheduling-wizard',
                label: 'Scheduling Wizard',
                hidden:
                  !hasAccessToAction('venture.wizard') ||
                  isMentorNotParticipant,
                component: <CreateSchedulingWizardButton venture={venture} />,
              },
              ...linkActions.map(
                ({ id, label, href, access, startIcon, isContained }) => ({
                  key: id,
                  label,
                  hidden:
                    !hasAccessToAction(access) ||
                    location.pathname.includes(
                      getRoutePath(href, { ventureId }),
                    ),
                  component: (
                    <MaterialButton
                      component={Link}
                      to={getRoutePath(href, { ventureId })}
                      variant={isContained ? 'contained' : 'outlined'}
                      color='primary'
                      className={classes.linkButton}
                      startIcon={startIcon}
                      data-testid={`button-${id}`}
                    >
                      {label}
                    </MaterialButton>
                  ),
                }),
              ),
              {
                key: 'request-profile-update',
                label: 'Request Profile Update',
                hidden: !hasAccessToAction('venture.updateProfile'),
                component: (
                  <ConfirmButton
                    title='Send Single Profile Update Request'
                    loading={sending}
                    body='Your are about to send a temporary link to allow participants to update their profile. The link can work only 1 time, and is available for 48 hours only.'
                    successProps={{
                      btnLabel: 'Ok',
                      onSuccess: handleSendVentureUpdate,
                    }}
                    cancelProps={{
                      btnLabel: 'Cancel',
                    }}
                    buttonRenderer={({ onClick }) => (
                      <Button
                        className={classes.requestLink}
                        onClick={onClick}
                        variant='contained'
                        data-testid='button-invite-venture-update'
                      >
                        Request Profile Update
                      </Button>
                    )}
                  />
                ),
              },
              {
                key: 'enrich-profile',
                label: 'Enrich Profile',
                hidden: !hasAccessToAction('venture.updateProfile'),
                component: (
                  <Button
                    onClick={() => setIsEnrichModalOpen(true)}
                    variant='contained'
                    data-testid='button-enrich-profile'
                  >
                    Enrich Profile
                  </Button>
                ),
              },
              {
                key: 'delete',
                label: 'Delete',
                hidden: !hasAccessToAction('venture.delete'),
                component: <DeleteButton type='venture' id={ventureId} />,
              },
            ]}
          />
          <Paper className={classes.container} elevation={1}>
            {!isReportPage && <PageTabs ventureId={ventureId} />}
            <div className={classes.content}>
              <Switch>
                <RouteACL
                  exact
                  path={Pages.VENTURE_DETAILS}
                  component={VentureDetailsPage}
                  rule='venture.details.view'
                  redirectUrl={Pages.ACTIVE_VENTURES}
                />
                <RouteACL
                  exact
                  path={Pages.VENTURE_FOUNDERS}
                  component={VentureFoundersPage}
                  rule='venture.founders.view'
                  redirectUrl={Pages.ACTIVE_VENTURES}
                />
                <RouteACL
                  exact
                  path={Pages.VENTURE_ASSIGNMENTS}
                  component={VentureAssignmentsPage}
                  rule='venture.assignemnts.view'
                  redirectUrl={Pages.ACTIVE_VENTURES}
                />
                <RouteACL
                  exact
                  path={Pages.VENTURE_MENTOR_HISTORY}
                  component={VentureMentorHistoryPage}
                  rule='venture.mentorHistory.view'
                  redirectUrl={Pages.ACTIVE_VENTURES}
                />
                <RouteACL
                  exact
                  path={Pages.VENTURE_NOTES}
                  component={VentureNotesPage}
                  rule='venture.notes.view'
                  redirectUrl={Pages.ACTIVE_VENTURES}
                />
                <RouteACL
                  exact
                  path={Pages.VENTURE_LOG}
                  component={VentureLogPage}
                  redirectUrl={Pages.ACTIVE_VENTURES}
                  rule='venture.logs.view'
                />
                <RouteACL
                  exact
                  path={Pages.VENTURE_ADDITIONAL_INFO}
                  component={VentureAdditionalInfoPage}
                  rule='venture.additionalInfo.view'
                  redirectUrl={Pages.ACTIVE_VENTURES}
                />
                <RouteACL
                  exact
                  path={Pages.VENTURE_GOALS}
                  component={VentureGoalsPage}
                  rule='venture.goals.view'
                  redirectUrl={Pages.ACTIVE_VENTURES}
                />
                <RouteACL
                  exact
                  path={Pages.VENTURE_QUARTERLY_REPORT}
                  component={VentureQuarterlyReportPage}
                  rule='venture.finance.view'
                  redirectUrl={Pages.ACTIVE_VENTURES}
                />
                <RouteACL
                  exact
                  path={Pages.VENTURE_QUARTERLY_REPORT_FORM}
                  component={VentureQuarterlyReportFormPage}
                  rule='venture.details.quarterReport'
                  redirectUrl={Pages.ACTIVE_VENTURES}
                />
                <Redirect to={Pages.VENTURE_DETAILS} />
              </Switch>
            </div>
          </Paper>
          <EnrichProfileModal
            open={isEnrichModalOpen}
            onClose={handleCloseEnrichModal}
            type='venture'
            entityId={ventureId}
            onSuccess={handleEnrichSuccess}
          />
        </>
      )}
    </BaseLayout>
  );
}

function withVentureProvider<P>(Component: React.FunctionComponent<P>) {
  return (props: P) => (
    <VentureProvider>
      <Component {...props} />
    </VentureProvider>
  );
}

export default withVentureProvider(VenturePage);
